/**
 * Utility functions for managing tutorial state in localStorage and cookies
 * 
 * IMPORTANT: Uses cookies as fallback for enterprise environments where localStorage 
 * is cleared on browser close due to security policies. Unfortunately IndexedDB is also 
 * cleared despite "cached files" being allowed.
 */

const TUTORIAL_STORAGE_KEY = 'aicoe-navigator-tutorial-completed';
const TUTORIAL_COOKIE_KEY = 'aicoe_navigator_tutorial_completed';

/**
 * Get a cookie value by name
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

/**
 * Set a cookie with a long expiration (1 year)
 */
function setCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  
  const date = new Date();
  date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000)); // 1 year
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
}

/**
 * Check if the user has completed the tutorial
 * Checks localStorage first, then cookies as fallback
 * @returns true if tutorial has been seen, false otherwise
 */
export function hasSeenTutorial(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    // Try localStorage first (fastest)
    const localValue = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    
    // Try cookie as fallback
    const cookieValue = getCookie(TUTORIAL_COOKIE_KEY);
    
    return localValue === 'true' || cookieValue === 'true';
  } catch (error) {
    console.error('Error reading tutorial state:', error);
    return false;
  }
}

/**
 * Mark the tutorial as completed
 * Saves to both localStorage AND cookies for enterprise environment compatibility
 */
export function markTutorialAsCompleted(): void {
  if (typeof window === 'undefined') return;
  
  try {
    // Save to localStorage
    localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    
    // Also save to cookie as fallback (1 year expiration)
    setCookie(TUTORIAL_COOKIE_KEY, 'true');
  } catch (error) {
    console.error('Error saving tutorial state:', error);
  }
}

/**
 * Reset the tutorial state (useful for testing or "What's New" feature)
 */
export function resetTutorialState(): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(TUTORIAL_STORAGE_KEY);
    document.cookie = `${TUTORIAL_COOKIE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  } catch (error) {
    console.error('Error resetting tutorial state:', error);
  }
}
